import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Chip, Button,
} from '@mui/material';
import {
  School as SchoolIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import cantinasService, { Escola } from '../../services/cantinas.service';
import CantinasModal from './EscolaModal';

const CantinasAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = () => {
    setLoading(true);
    cantinasService.getEscolas()
      .then(setEscolas)
      .catch(() => setError('Erro ao carregar escolas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totalAlunos = escolas.reduce((s, e) => s + (e.total_pulseiras || 0), 0);
  const totalCantinas = escolas.reduce((s, e) => s + (e.total_cantinas || 0), 0);

  const modules = [
    { title: 'Escolas', desc: 'Gerir escolas e cantinas', icon: <SchoolIcon />, path: '/cantinas', color: '#6233A0' },
    { title: 'Pulseiras', desc: 'Pulseiras NFC dos alunos', icon: <CreditCardIcon />, path: '/cantinas', color: '#0D7F9F' },
    { title: 'Transações', desc: 'Pagamentos e carregamentos', icon: <CreditCardIcon />, path: '/cantinas', color: '#10B981' },
  ];

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <RestaurantIcon sx={{ fontSize: 32, color: '#6233A0' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>Cantinas Escolares</Typography>
            <Typography variant="body2" color="text.secondary">Pagamento NFC em cantinas</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          sx={{ bgcolor: '#6233A0', '&:hover': { bgcolor: '#4e2880' } }}
          onClick={() => setModalOpen(true)}>
          Nova Escola
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats strip */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Escolas', value: escolas.length, icon: <SchoolIcon />, color: '#6233A0' },
          { label: 'Cantinas', value: totalCantinas, icon: <RestaurantIcon />, color: '#0D7F9F' },
          { label: 'Alunos activos', value: totalAlunos, icon: <PeopleIcon />, color: '#10B981' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}>
                {s.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Escola cards */}
      {escolas.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">Nenhuma escola registada. Cria a primeira escola para começar.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {escolas.map(e => (
            <Grid item xs={12} sm={6} md={4} key={e.id}>
              <Card sx={{ borderRadius: 3, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
                onClick={() => navigate(`/cantinas/${e.id}`)}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#6233A018', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <SchoolIcon sx={{ color: '#6233A0' }} />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography fontWeight={700} noWrap>{e.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{e.cidade}</Typography>
                    </Box>
                    <Chip label={e.ativo ? 'Activa' : 'Inactiva'} size="small"
                      color={e.ativo ? 'success' : 'default'} />
                  </Box>
                  <Box display="flex" gap={3}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{e.total_cantinas}</Typography>
                      <Typography variant="caption" color="text.secondary">cantinas</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{e.total_pulseiras}</Typography>
                      <Typography variant="caption" color="text.secondary">pulseiras</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CantinasModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} />
    </Box>
  );
};

export default CantinasAdminDashboard;
